import type { Commande, Configuration, Livreur, StatutCommande } from './types'

/**
 * Aperçu de développement — jamais livré.
 *
 * `import.meta.env.DEV` vaut `false` au build : `APERCU` est alors la constante
 * `false`, et tout ce qui n'est atteignable que par lui disparaît du bundle.
 * Il alimente l'écran Config avec des données fictives (et applique les
 * écritures en mémoire) pour pouvoir le VOIR sans se connecter à la base.
 */
export const APERCU: boolean =
  import.meta.env.DEV && new URLSearchParams(location.search).has('apercu')

/** `?apercu&sans0043` simule une base de production sans la migration 0043. */
const SANS_0043 = APERCU && new URLSearchParams(location.search).has('sans0043')

let courante: Configuration | null = null

export function apercuConfig(): Configuration {
  courante ??= {
    fraisService: 250,
    seuilPetiteCommande: 3000,
    fraisPetiteCommande: 500,
    seuilLivraisonOfferte: 15000,
    pointeMidi: '12:00-14:00',
    pointeSoir: '19:00-21:00',
    bonusPointe: 300,
    seuilVolume: 10,
    bonusVolume: 1000,
    seuilFidelite: 100,
    bonusFidelite: 50,
    premiumMensuel: 2500,
    jourPaie1: 1,
    jourPaie2: 16,
    noteAuto: 4.5,
    commissionParDefaut: 15,
    tvaActive: false,
    tvaTaux: 18,
    paliersLivraison: [
      [0, 3, 900, 800, 0],
      [3, 6, 1400, 1200, 200],
      [6, 10, 1900, 1800, 400],
      [10, 99, 2700, 2500, 500],
    ].map(([minKm, maxKm, fraisClient, partLivreur, fraisPremium], i) => ({
      id: String(i + 1),
      libelle: `${minKm} — ${maxKm} km`,
      minKm: minKm!,
      maxKm: maxKm!,
      fraisClient: fraisClient!,
      partLivreur: partLivreur!,
      fraisPremium: fraisPremium!,
    })),
    bonusMeteo: [
      { id: '1', condition: 'normal', bonusLivreur: 0, supplementClient: 0, premiumExempt: true },
      { id: '2', condition: 'rain', bonusLivreur: 500, supplementClient: 500, premiumExempt: true },
      { id: '3', condition: 'extreme_heat', bonusLivreur: 300, supplementClient: 300, premiumExempt: false },
    ],
    meteoActuelle: SANS_0043 ? null : 'normal',
    fraisTraversee: SANS_0043 ? null : 500,
    bonusTraversee: SANS_0043 ? null : 500,
    fraisMM: { encaissementWave: 1, encaissementOm: null, decaissementWave: 1, decaissementOm: null },
    minimaLivraison: SANS_0043 ? null : { client: 1000, premium: 400, driver: 650 },
    fraisServiceHaut: null,
    reductionPremium: SANS_0043 ? null : 50,
    commandeMinimum: SANS_0043 ? null : 1000,
    tension: SANS_0043
      ? null
      : { active: false, rayonKm: 3, seuil: 1, pentePct: 20, plafondPct: 30, partLivreurPct: 80 },
  }
  return courante
}

/** Applique en mémoire ce que l'écran aurait écrit en base. */
export function apercuAppliquer(patch: Record<string, unknown>): void {
  const c = apercuConfig()
  if ('meteo_actuelle' in patch) c.meteoActuelle = patch.meteo_actuelle as Configuration['meteoActuelle']
  if ('frais_traversee' in patch) c.fraisTraversee = Number(patch.frais_traversee)
  if ('bonus_traversee' in patch) c.bonusTraversee = Number(patch.bonus_traversee)
  const mm: Array<[string, keyof Configuration['fraisMM']]> = [
    ['frais_mm_encaissement_wave', 'encaissementWave'],
    ['frais_mm_encaissement_om', 'encaissementOm'],
    ['frais_mm_decaissement_wave', 'decaissementWave'],
    ['frais_mm_decaissement_om', 'decaissementOm'],
  ]
  for (const [col, champ] of mm) {
    if (col in patch) {
      const v = String(patch[col] ?? '').trim()
      c.fraisMM[champ] = v === '' ? null : Number(v.replace(',', '.'))
    }
  }
  if ('minima' in patch) c.minimaLivraison = patch.minima as Configuration['minimaLivraison']
  if ('fraisService' in patch) {
    const f = patch.fraisService as { bas: number; haut: number | null }
    c.fraisService = f.bas
    c.fraisServiceHaut = f.haut
  }
  if ('tension_active' in patch && c.tension) c.tension.active = patch.tension_active === 'oui'
  if ('premium_reduction_pct' in patch) c.reductionPremium = Number(String(patch.premium_reduction_pct).replace(',', '.'))
  if ('commande_minimum' in patch) c.commandeMinimum = Number(patch.commande_minimum)
  const tension: Array<[string, keyof NonNullable<Configuration['tension']>]> = [
    ['tension_rayon_km', 'rayonKm'],
    ['tension_seuil', 'seuil'],
    ['tension_pente_pct', 'pentePct'],
    ['tension_plafond_pct', 'plafondPct'],
    ['tension_part_livreur_pct', 'partLivreurPct'],
  ]
  for (const [col, champ] of tension) {
    if (col in patch && c.tension) (c.tension[champ] as number) = Number(String(patch[col]).replace(',', '.'))
  }
  if ('paliers' in patch) {
    const liste = patch.paliers as Array<{
      id: string | null; minKm: number; maxKm: number; fraisClient: number; partLivreur: number; fraisPremium: number
    }>
    let suivant = Math.max(0, ...c.paliersLivraison.map((p) => Number(p.id))) + 1
    c.paliersLivraison = liste.map((p) => ({
      id: p.id ?? String(suivant++),
      libelle: `${p.minKm} — ${p.maxKm} km`,
      minKm: p.minKm,
      maxKm: p.maxKm,
      fraisClient: p.fraisClient,
      partLivreur: p.partLivreur,
      fraisPremium: p.fraisPremium,
    }))
  }
}

// ── Livreurs et commandes fictifs (écrans Live, Livreurs) ────────────────

let livreurs: Livreur[] | null = null

export function apercuLivreurs(): Livreur[] {
  const base = {
    typeVehicule: 'moto', modele: 'Honda', fournisseurPaie: 'wave' as const, numeroPaie: '+22370000000',
    verifie: true, creeLe: '2026-09-01T10:00:00Z',
  }
  livreurs ??= [
    { ...base, id: 'apercu-l1', nom: 'Moussa Konaté', telephone: '+22370111111', plaque: 'BA 1234', etat: 'online',
      note: 4.6, noteMixte: 4.5, avis: 3, sansAvis: 5, livraisons: 8, lat: 12.6561, lng: -7.9885 },
    { ...base, id: 'apercu-l2', nom: 'Issa Traoré', telephone: '+22370222222', plaque: 'BA 5678', etat: 'online',
      note: 5, noteMixte: null, avis: 0, sansAvis: 0, livraisons: 0, lat: 0, lng: 0 },
    { ...base, id: 'apercu-l3', nom: 'Awa Diarra', telephone: '+22370333333', plaque: 'BA 9012', etat: 'online',
      note: 4.2, noteMixte: 4.2, avis: 1, sansAvis: 0, livraisons: 1, lat: 12.6, lng: -8.0167 },
    { ...base, id: 'apercu-l4', nom: 'Seydou Coulibaly', telephone: '+22370444444', plaque: 'BA 3456', etat: 'busy',
      note: 4.8, noteMixte: 4.7, avis: 12, sansAvis: 2, livraisons: 14, lat: 12.64, lng: -8.0 },
    { ...base, id: 'apercu-l5', nom: 'Fanta Sidibé', telephone: '+22370555555', plaque: 'BA 7890', etat: 'online',
      note: 4.4, noteMixte: 4.4, avis: 2, sansAvis: 1, livraisons: 3, lat: 12.6392, lng: -8.0029 },
  ]
  return livreurs
}

let commandes: Commande[] | null = null

export function apercuCommandes(): Commande[] {
  if (commandes) return commandes
  const nul = {
    accepteeLe: null, enPreparationLe: null, preteLe: null, livreurAffecteLe: null, retireeLe: null,
    enLivraisonLe: null, livreeLe: null, annuleeLe: null,
    horodatages: { accepted: null, preparing: null, ready: null, rider_assigned: null, delivering: null, picked_up: null, delivered: null, cancelled: null },
  }
  const mk = (n: number, statut: StatutCommande, total: number, marchand: string, lat: number | null, lng: number | null, livreur: string | null): Commande => ({
    id: `apercu-c${n}`, ref: `EAT-APC0${n}`, statut, total, ...nul,
    fraisLivraison: 1000, partLivreur: 800, fraisService: 250, fraisPetiteCommande: 0, fraisMeteo: 0,
    clientId: 'c', clientNom: `Client ${n}`, clientTelephone: '+22376000000', clientQuartier: 'Hamdallaye',
    clientLat: null, clientLng: null, adresseLivraison: 'Rue 123, Hamdallaye ACI', communeLivraison: 'Commune IV', noteLivraison: '',
    marchandId: `m${n}`, marchandNom: marchand, marchandLat: lat, marchandLng: lng,
    livreurId: livreur ? `apercu-${livreur}` : null,
    livreurNom: livreur ? 'Moussa Konaté' : null,
    articles: [{ name: 'Poulet braisé', quantity: 2, unit_price: 3500, total: 7000, commission_pct: 0.15 }],
    creeeLe: new Date(Date.now() - n * 7 * 60000).toISOString(),
  })
  commandes = [
    mk(1, 'ready', 8750, 'Le Relais de la Médina', 12.6392, -8.0029, null),
    mk(2, 'accepted', 12400, 'Grillades du Fleuve (sans position)', null, null, null),
    mk(3, 'paid', 5250, 'Chez Awa', 12.6561, -7.9885, null),
    mk(4, 'rider_assigned', 9100, 'Le Relais de la Médina', 12.6392, -8.0029, 'l1'),
    mk(5, 'picked_up', 6800, 'Chez Awa', 12.6561, -7.9885, 'l1'),
  ]
  return commandes
}

export function apercuAffecter(commandeId: string, livreurId: string): void {
  const c = apercuCommandes().find((x) => x.id === commandeId)
  const l = apercuLivreurs().find((x) => x.id === livreurId)
  if (!c || !l) throw new Error('Cette commande n\'existe plus. Rechargez la page.')
  if (c.livreurId) throw new Error('Un livreur est déjà affecté à cette commande. Rechargez la page.')
  c.livreurId = l.id
  c.livreurNom = l.nom
  c.statut = c.statut === 'ready' ? 'ready' : 'rider_assigned'
  l.etat = 'busy'
}

/** Reproduit le refus du serveur pour un motif vide — et lui seul. */
export function apercuAnnuler(commandeId: string, motif: string): void {
  const c = apercuCommandes().find((x) => x.id === commandeId)
  if (!c) throw new Error('Cette commande n\'existe plus. Rechargez la page.')
  if (!motif.trim()) throw new Error('Un motif est obligatoire pour annuler une commande.')
  c.montantRembourse = c.statut === 'pending' ? 0 : c.total
  c.statut = 'cancelled'
  c.annuleeLe = new Date().toISOString()
}
